import { Category } from "@faire/deprecated-retailer-visitor-shared/models/Category/Category";
import { useSettingNextJSRscCategoryPage } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { CategoryPageRequestParamsProvider } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/CategoryPageRequestParamsProvider";
import { useLoggedInCategoryPageBase } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/LazyLoggedInCategoryPageBase";
import { useSDUICategoryPageBase } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/LazySDUICategoryPageBase";
import { useShowProductFirstResults } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/useShowProductFirstResults/useShowProductFirstResults";
import { CategoryPagePrefetcher } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/CategoryPagePrefetcher";
import { CurrentCategoryPageContextProvider } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/currentCategoryPageContext";
import { SDUICategoryPagePrefetcher } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/SDUICategoryPagePrefetcher";
import { useAssignMigrateCatnavToV3LayoutApis } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useAssignMigrateCatnavToV3LayoutApis";
import { useAssignRSCM2CategoryPage } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useAssignRSCM2CategoryPage";
import { useIsSDUISeasonalHubPage } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useIsSDUISeasonalHubPage";
import { LoggedInCategoryPageLoadingSkeleton } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/CategoryPageLoadingSkeleton/LoggedInCategoryPageLoadingSkeleton";
import { isRSCCategoryNavigationPath } from "@faire/deprecated-retailer-visitor-shared/views/category/HubsRSC/isRSCCategoryNavigationPath";
import { TelemetryManager } from "@faire/web/common/opentelemetry/TelemetryManager";
import { isLocal } from "@faire/web/common/user-agent/isLocal";
import React, { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { LoggedInCategoryPageErrorFallback } from "./LoggedInCategoryPageErrorFallback";

interface IProps {
  category: Category;
  navigationPath: string;
}

const getTelemetryTraceName = (
  isSDUISeasonalHubPage: boolean,
  isProductFirstCategory: boolean
) => {
  if (isSDUISeasonalHubPage) {
    return "uplt-page-category-hub-logged-in";
  }

  return isProductFirstCategory
    ? "uplt-page-category-product-logged-in"
    : "uplt-page-category-brand-logged-in";
};

export const LoggedInCategoryPage = (props: IProps) => {
  const { category, navigationPath } = props;

  const isCategoryPageRSCSettingEnabled = useSettingNextJSRscCategoryPage();
  const isCategoryPageRSCEnabled =
    isRSCCategoryNavigationPath(navigationPath) &&
    isCategoryPageRSCSettingEnabled;

  // this is to prevent unexpected code being executed when navigate from non rsc category page to rsc category page
  // skip local environment because local environment doesn't have header, and user will see empty page unexpectedly
  // we have to manually set the header in our local environment
  if (isCategoryPageRSCEnabled && !isLocal()) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<LoggedInCategoryPageErrorFallback />}>
      <CurrentCategoryPageContextProvider value={{ category, navigationPath }}>
        <CategoryPageRequestParamsProvider>
          <LoggedInCategoryPageBase />
        </CategoryPageRequestParamsProvider>
      </CurrentCategoryPageContextProvider>
    </ErrorBoundary>
  );
};

const LoggedInCategoryPageBase = () => {
  const LoggedInCategoryPageBase = useLoggedInCategoryPageBase();
  const SDUICategoryPageBase = useSDUICategoryPageBase();
  const isProductFirstCategory = useShowProductFirstResults();
  const isSDUISeasonalHubPage = useIsSDUISeasonalHubPage();
  const shouldRenderSDUICategoryPage =
    isSDUISeasonalHubPage || isProductFirstCategory;

  TelemetryManager.get().startTrace(
    getTelemetryTraceName(isSDUISeasonalHubPage, isProductFirstCategory)
  );

  useAssignRSCM2CategoryPage();
  useAssignMigrateCatnavToV3LayoutApis();

  return (
    <>
      {shouldRenderSDUICategoryPage ? (
        <SDUICategoryPagePrefetcher />
      ) : (
        <CategoryPagePrefetcher />
      )}

      <Suspense
        fallback={
          <LoggedInCategoryPageLoadingSkeleton
            isFullWidthCategoryPage={!isSDUISeasonalHubPage}
          />
        }
      >
        {shouldRenderSDUICategoryPage ? (
          <SDUICategoryPageBase />
        ) : (
          <LoggedInCategoryPageBase />
        )}
      </Suspense>
    </>
  );
};
